/* The SLA clock.
 *
 * Pure arithmetic over a working calendar. Two rules, both from the spec:
 *
 *   1. The clock runs on IT'S hours — 07:00–17:00 Mon–Fri, paused on IHS
 *      holidays — because the target measures IT's response, not the
 *      requester's day. One company-wide calendar, not per-brand.
 *   2. The clock PAUSES whenever the ticket is Waiting, so chasing a requester
 *      or a supplier never counts against IT.
 *
 * Per-priority `aroundClock` opts a priority out of the calendar entirely, so
 * P1 does not wait for Monday while P2–P4 do.
 *
 * The prototype computes in the browser's local time, which for everyone at IHS
 * is Mountain. The backend version will be explicitly zoned rather than relying
 * on the host's clock — noted so the port does not quietly inherit this.
 */

const MS_PER_MIN = 60_000;
const MAX_DAY_HOPS = 400; // ~14 months of weekends and holidays. A bound, not a limit.

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function isWorkingDay(date, cal) {
  return cal.workingDays.includes(date.getDay()) && !cal.holidays.includes(ymd(date));
}

export function isWorkingTime(date, cal) {
  if (!isWorkingDay(date, cal)) return false;
  const mins = date.getHours() * 60 + date.getMinutes();
  return mins >= cal.openHour * 60 && mins < cal.closeHour * 60;
}

/** The next instant the desk is open. Returns `date` unchanged if it already is. */
export function nextWorkingOpen(date, cal) {
  let cursor = new Date(date);
  if (isWorkingTime(cursor, cal)) return cursor;

  // Before opening on a working day → jump to this morning's open.
  if (isWorkingDay(cursor, cal) && cursor.getHours() < cal.openHour) {
    cursor.setHours(cal.openHour, 0, 0, 0);
    return cursor;
  }

  // Otherwise walk forward a day at a time to the next open day's opening.
  for (let hop = 0; hop < MAX_DAY_HOPS; hop += 1) {
    cursor.setDate(cursor.getDate() + 1);
    cursor.setHours(cal.openHour, 0, 0, 0);
    if (isWorkingDay(cursor, cal)) return cursor;
  }

  // Unreachable with a sane calendar. Returning the cursor rather than throwing
  // keeps a bad holiday list from taking the queue down.
  return cursor;
}

/**
 * Add `minutes` of WORKING time to `start`.
 * `aroundClock` bypasses the calendar entirely (P1).
 */
export function addWorkingMinutes(start, minutes, cal, aroundClock = false) {
  if (aroundClock) return new Date(start.getTime() + minutes * MS_PER_MIN);

  let cursor = nextWorkingOpen(new Date(start), cal);
  let left = minutes;

  for (let hop = 0; hop < MAX_DAY_HOPS && left > 0; hop += 1) {
    const close = new Date(cursor);
    close.setHours(cal.closeHour, 0, 0, 0);

    const availableToday = Math.max(0, (close.getTime() - cursor.getTime()) / MS_PER_MIN);

    if (left <= availableToday) {
      return new Date(cursor.getTime() + left * MS_PER_MIN);
    }

    left -= availableToday;
    cursor = nextWorkingOpen(new Date(close.getTime() + MS_PER_MIN), cal);
  }

  return cursor;
}

/** Working minutes elapsed between two instants. */
export function workingMinutesBetween(from, to, cal, aroundClock = false) {
  if (aroundClock) return Math.max(0, (to.getTime() - from.getTime()) / MS_PER_MIN);
  if (to <= from) return 0;

  let cursor = new Date(from);
  let total = 0;

  for (let hop = 0; hop < MAX_DAY_HOPS; hop += 1) {
    if (cursor >= to) break;
    if (!isWorkingTime(cursor, cal)) {
      cursor = nextWorkingOpen(cursor, cal);
      if (cursor >= to) break;
    }
    const close = new Date(cursor);
    close.setHours(cal.closeHour, 0, 0, 0);
    const segmentEnd = close < to ? close : to;
    total += Math.max(0, (segmentEnd.getTime() - cursor.getTime()) / MS_PER_MIN);
    cursor = new Date(close.getTime() + MS_PER_MIN);
  }

  return total;
}

/**
 * Where a ticket stands against its targets.
 *
 * `pausedMins` is the working time the ticket has spent in Waiting. It is
 * subtracted from elapsed rather than added to the due date, so a ticket that
 * bounces in and out of Waiting five times still gets exactly the working time
 * it was actually blocked for.
 *
 * @returns {{state: 'met'|'ok'|'at_risk'|'breached', usedMins, targetMins, remainingMins}}
 */
export function slaStanding({ raisedAt, stoppedAt, pausedMins = 0, targetMins, cal, aroundClock, now }) {
  const end = stoppedAt ? new Date(stoppedAt) : new Date(now || Date.now());
  const used = Math.max(0, workingMinutesBetween(new Date(raisedAt), end, cal, aroundClock) - pausedMins);
  const remaining = targetMins - used;

  let state;
  if (stoppedAt) state = remaining >= 0 ? 'met' : 'breached';
  else if (remaining < 0) state = 'breached';
  // "At risk" is the last 20% of the target — early enough to act on, late
  // enough that the queue is not permanently amber.
  else if (remaining <= targetMins * 0.2) state = 'at_risk';
  else state = 'ok';

  return { state, usedMins: Math.round(used), targetMins, remainingMins: Math.round(remaining) };
}

/** "15 min" · "4 hours" · "3 working days" — targets read as people say them. */
export function formatTarget(minutes, aroundClock = false) {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 600) {
    const h = minutes / 60;
    return `${Number.isInteger(h) ? h : h.toFixed(1)} ${h === 1 ? 'hour' : 'hours'}`;
  }
  const days = minutes / 600; // a working day is 10 hours on this calendar
  const label = aroundClock ? 'days' : 'working days';
  return `${Number.isInteger(days) ? days : days.toFixed(1)} ${days === 1 ? label.replace('days', 'day') : label}`;
}

/** "2h 40m left" · "1h 5m over" — for the queue and the ticket rail. */
export function formatRemaining(mins) {
  const over = mins < 0;
  const abs = Math.abs(Math.round(mins));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const body = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return over ? `${body} over` : `${body} left`;
}
